import { Badge } from '@amore/ui/components/badge'
import { Button } from '@amore/ui/components/button'
import { ViewToggle, ViewToggleItem } from '@amore/ui/components/view-toggle'
import { useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { ArrowRight, CalendarRange, Check, List, Plus, Wallet } from 'lucide-react'
import { useState } from 'react'
import { formatDate, formatMoney, isOverdue, plural, relativeDue } from '@/lib/format'
import { supplierCategoryLabels } from '@/lib/labels'
import { useCanEdit, useUpdate, useWedding } from '@/mock/store'
import type { Expense, WeddingData } from '@/mock/types'
import { type Column, DataTable, type TableGroup } from '@/patterns/data-table'
import { EmptyState } from '@/patterns/empty-state'
import { FilterBar } from '@/patterns/filter-bar'
import { PromptList } from '@/patterns/prompts'
import { Screen } from '@/patterns/screen'
import { StatSummary } from '@/patterns/stat-summary'
import { categoryLabel, categoryOrder } from '@/screens/suppliers/model'
import { useToast } from '@/shell/toaster'
import budgetIllustration from './assets/budget.svg'
import { TotalDialog } from './budget-dialogs'
import { ExpenseSheet, type ExpenseTab } from './expense-sheet'
import {
  type BudgetFilter,
  type BudgetView,
  budgetFilters,
  budgetTotals,
  expenseCategory,
  expenseStatus,
  expenseTitle,
  filterLabels,
  markPaid,
  matchesFilter,
  matchesSearch,
  nextPayment,
  paidAmount,
  parseFilter,
  parseView,
  statusLabels,
  statusTones,
} from './model'
import { PaymentTimeline } from './payment-timeline'
import { type BudgetPromptAction, budgetPrompts } from './prompts'
import { BudgetSuggestions } from './suggestions'

export function BudgetScreen() {
  const data = useWedding()
  const canEdit = useCanEdit()
  const navigate = useNavigate()
  const { expenseId } = useParams({ strict: false }) as { expenseId?: string }
  const search = useSearch({ strict: false }) as {
    filter?: string
    q?: string
    tab?: string
    add?: string
  }
  const [adding, setAdding] = useState<{ supplierId?: string } | null>(null)
  const empty = data.budget.expenses.length === 0 && data.budget.total === null
  const openExpense = data.budget.expenses.find((expense) => expense.id === expenseId)
  // `?add=<supplierId>` comes from My suppliers: a new item prefilled with that supplier.
  const { tab: sheetTab, add: addSupplier, ...listSearch } = search
  const open = (id: string, initialTab?: ExpenseTab) =>
    void navigate({
      to: `/budget/${id}`,
      search: { ...listSearch, ...(initialTab === 'payments' ? { tab: initialTab } : {}) },
    })
  const add = (supplierId?: string) => setAdding({ supplierId })
  const [settingTotal, setSettingTotal] = useState(false)
  const setTotal = () => setSettingTotal(true)

  return (
    <Screen
      title="Rozpočet"
      illustration={<img src={budgetIllustration} alt="" className="h-auto w-full" />}
      description="Kolik svatba stojí, co je zaplacené a co vás ještě čeká."
      actions={
        canEdit && !empty ? (
          <>
            <Button appearance="outline" tone="neutral" onClick={setTotal}>
              <Wallet aria-hidden="true" className="size-4" />
              {data.budget.total === null ? 'Nastavit rozpočet' : 'Upravit rozpočet'}
            </Button>
            <Button onClick={() => add()}>
              <Plus aria-hidden="true" className="size-4" />
              Přidat položku
            </Button>
          </>
        ) : null
      }
    >
      {empty ? (
        <>
          <EmptyState
            icon={<img src={budgetIllustration} alt="" className="h-28 w-32" />}
            title="Rozpočet je zatím prázdný"
            description="Nastavte si, kolik chcete za svatbu utratit, a přidávejte položky, jak budete domlouvat dodavatele."
            actions={
              canEdit ? (
                <>
                  <Button onClick={setTotal}>Nastavit rozpočet</Button>
                  <Button appearance="outline" tone="neutral" onClick={() => add()}>
                    Přidat položku
                  </Button>
                </>
              ) : null
            }
          />
          {canEdit ? <BudgetSuggestions onOpen={open} /> : null}
        </>
      ) : (
        <BudgetItems data={data} onOpen={open} onAdd={add} onTotal={setTotal} />
      )}

      {openExpense ? (
        <ExpenseSheet
          key={openExpense.id}
          expense={openExpense}
          initialTab={sheetTab === 'payments' ? 'payments' : 'item'}
          onClose={() => void navigate({ to: '/budget', search: listSearch })}
        />
      ) : null}
      {adding ? (
        <ExpenseSheet supplierId={adding.supplierId} onClose={() => setAdding(null)} />
      ) : null}
      {canEdit && addSupplier && data.suppliers.some((supplier) => supplier.id === addSupplier) ? (
        <ExpenseSheet
          key={addSupplier}
          supplierId={addSupplier}
          onClose={() => void navigate({ to: '/budget', search: listSearch })}
        />
      ) : null}
      {settingTotal ? <TotalDialog onClose={() => setSettingTotal(false)} /> : null}
    </Screen>
  )
}

function BudgetItems({
  data,
  onOpen,
  onAdd,
  onTotal,
}: {
  data: WeddingData
  onOpen: (expenseId: string, tab?: ExpenseTab) => void
  onAdd: (supplierId?: string) => void
  onTotal: () => void
}) {
  const navigate = useNavigate()
  const canEdit = useCanEdit()
  const update = useUpdate()
  const { showToast } = useToast()
  const search = useSearch({ strict: false }) as { filter?: string; q?: string; view?: string }
  const filter = parseFilter(search.filter)
  const query = search.q ?? ''
  const view = parseView(search.view)
  const totals = budgetTotals(data)

  const setSearch = (next: { filter?: BudgetFilter; q?: string; view?: BudgetView }) => {
    const merged = { filter, q: query, view, ...next }
    void navigate({
      to: '/budget',
      search: {
        ...(merged.filter !== 'all' ? { filter: merged.filter } : {}),
        ...(merged.q ? { q: merged.q } : {}),
        ...(merged.view !== 'items' ? { view: merged.view } : {}),
      },
      replace: true,
    })
  }
  const toggleFilter = (value: BudgetFilter) =>
    setSearch({ filter: filter === value ? 'all' : value })

  const pay = (expenseId: string, paymentId: string) => {
    const before = data
    update((current) => markPaid(current, expenseId, paymentId))
    const expense = data.budget.expenses.find((item) => item.id === expenseId)
    const payment = expense?.payments.find((item) => item.id === paymentId)
    showToast({
      title: 'Platba zaplacena',
      description: expense && payment ? `${expenseTitle(expense, data)} · ${payment.name}` : '',
      tone: 'success',
      action: {
        label: 'Vrátit zpět',
        altText: 'Vrátit platbu mezi nezaplacené',
        onClick: () => update(() => before),
      },
    })
  }

  const visible = data.budget.expenses.filter(
    (expense) => matchesSearch(expense, data, query) && matchesFilter(expense, filter),
  )
  const groups: TableGroup<Expense>[] = categoryOrder.flatMap((category) => {
    const rows = visible.filter((expense) => expenseCategory(expense, data) === category)
    if (rows.length === 0) return []
    const price = rows.reduce((sum, expense) => sum + expense.amount, 0)
    const remaining = rows.reduce(
      (sum, expense) => sum + Math.max(expense.amount - paidAmount(expense), 0),
      0,
    )
    const fullyPaid = remaining === 0 && rows.every((expense) => expense.amount > 0)
    return [
      {
        id: category,
        label: categoryLabel(category),
        meta: items(rows.length),
        summary: {
          price: formatMoney(price),
          remaining: (
            <Badge tone={fullyPaid ? 'success' : remaining > 0 ? 'warning' : 'neutral'}>
              {fullyPaid ? <Check aria-hidden="true" className="size-3.5" /> : null}
              {remaining > 0 || fullyPaid ? formatMoney(remaining) : '—'}
            </Badge>
          ),
        },
        rows,
        collapsible: true,
      },
    ]
  })

  const over = totals.unallocated !== null && totals.unallocated < 0

  return (
    <>
      <StatSummary
        headline={
          totals.total === null
            ? undefined
            : {
                label: 'Celkový rozpočet',
                value: formatMoney(totals.total),
                caption:
                  totals.unallocated === null
                    ? undefined
                    : over
                      ? `přečerpáno o ${formatMoney(-totals.unallocated)}`
                      : `zbývá rozdělit ${formatMoney(totals.unallocated)}`,
              }
        }
        groups={[
          {
            title: 'Naplánováno',
            value:
              totals.total === null
                ? formatMoney(totals.planned)
                : `${Math.round((totals.planned / totals.total) * 100)} %`,
            progress:
              totals.total === null
                ? undefined
                : {
                    value: Math.min(totals.planned, totals.total),
                    max: totals.total,
                    tone: over ? 'warning' : 'accent',
                    label: `Naplánováno ${formatMoney(totals.planned)} z ${formatMoney(totals.total)}`,
                  },
            tiles: [
              {
                label: 'Dohodnuto',
                value: formatMoney(totals.agreed),
                caption: 'Potvrzené ceny',
              },
              {
                label: 'Odhadem',
                value: formatMoney(totals.estimated),
                caption: totals.estimates
                  ? `${items(totals.estimates)} ${plural(totals.estimates, 'čeká', 'čekají', 'čeká')} na cenu`
                  : 'Žádné odhady',
                onSelect: () => toggleFilter('estimate'),
                active: filter === 'estimate',
              },
            ],
          },
          ...(totals.total === null
            ? []
            : [
                {
                  title: 'Platby',
                  value: totals.planned
                    ? `${Math.round((totals.paid / totals.planned) * 100)} %`
                    : undefined,
                  progress: {
                    value: totals.paid,
                    max: Math.max(totals.planned, 1),
                    tone: 'success' as const,
                    label: `Zaplaceno ${formatMoney(totals.paid)} z ${formatMoney(totals.planned)}`,
                  },
                  tiles: [
                    {
                      label: 'Zaplaceno',
                      value: formatMoney(totals.paid),
                      tone: 'success' as const,
                      onSelect: () => toggleFilter('paid'),
                      active: filter === 'paid',
                    },
                    {
                      label: 'Zbývá zaplatit',
                      value: formatMoney(totals.remaining),
                      caption: totals.overdue ? (
                        <span className="font-semibold text-danger-foreground">
                          z toho {formatMoney(totals.overdue)} po splatnosti
                        </span>
                      ) : undefined,
                      onSelect: () => toggleFilter(totals.overdue ? 'overdue' : 'due'),
                      active: filter === 'due' || filter === 'overdue',
                    },
                  ],
                },
              ]),
        ]}
        aside={
          totals.total === null ? (
            <div className="flex h-full flex-col items-start justify-center gap-3">
              <p className="text-base font-semibold">Nastavte si celkový rozpočet</p>
              <p className="text-sm text-muted-foreground">
                Uvidíte, kolik vám zbývá rozdělit, a upozorníme vás, když plán přeroste.
              </p>
              {canEdit ? (
                <Button size="sm" onClick={onTotal}>
                  Nastavit rozpočet
                </Button>
              ) : null}
            </div>
          ) : undefined
        }
      />

      {canEdit ? (
        <PromptList
          prompts={budgetPrompts(data).map((prompt) => ({
            ...prompt,
            action: (
              <PromptAction
                action={prompt.action}
                onPaid={pay}
                onFilter={(value) => setSearch({ filter: value })}
                onOpen={onOpen}
                onAdd={onAdd}
                onTotal={onTotal}
              />
            ),
          }))}
          onHide={(code) =>
            update((current) => ({ ...current, hiddenPrompts: [...current.hiddenPrompts, code] }))
          }
        />
      ) : null}

      <FilterBar
        filters={budgetFilters.map((value) => ({ value, label: filterLabels[value] }))}
        value={filter}
        onChange={(value) => setSearch({ filter: value })}
        search={{
          value: query,
          onChange: (value) => setSearch({ q: value }),
          placeholder: view === 'timeline' ? 'Hledat platbu' : 'Hledat položku',
        }}
        actions={
          <ViewToggle aria-label="Zobrazení rozpočtu">
            <ViewToggleItem
              icon={<List aria-hidden="true" />}
              pressed={view === 'items'}
              onClick={() => setSearch({ view: 'items' })}
            >
              Položky
            </ViewToggleItem>
            <ViewToggleItem
              icon={<CalendarRange aria-hidden="true" />}
              pressed={view === 'timeline'}
              onClick={() => setSearch({ view: 'timeline' })}
            >
              Časová osa
            </ViewToggleItem>
          </ViewToggle>
        }
      />

      {view === 'timeline' ? (
        <PaymentTimeline
          data={data}
          filter={filter}
          query={query}
          onOpen={(expenseId) => onOpen(expenseId, 'payments')}
          onPay={pay}
          empty={
            data.budget.expenses.some((expense) => expense.payments.length > 0) ? (
              <div className="space-y-3">
                <p className="font-semibold text-foreground">Žádná platba neodpovídá</p>
                <p className="text-sm">Zkuste hledat jinak nebo zobrazit všechny platby.</p>
                <Button
                  appearance="outline"
                  tone="neutral"
                  size="sm"
                  onClick={() => setSearch({ filter: 'all', q: '' })}
                >
                  Zobrazit všechny platby
                </Button>
              </div>
            ) : (
              <div className="space-y-1">
                <p className="font-semibold text-foreground">Zatím tu nejsou žádné platby</p>
                <p className="text-sm">
                  Zálohy a doplatky rozepíšete v detailu položky, tady je pak uvidíte po měsících.
                </p>
              </div>
            )
          }
        />
      ) : (
        <DataTable
          aria-label="Položky rozpočtu podle kategorií"
          columns={budgetColumns(data)}
          getRowId={(expense) => expense.id}
          onRowClick={(expense) => onOpen(expense.id)}
          rowTone={(expense) => (expenseStatus(expense) === 'overdue' ? 'danger' : undefined)}
          groups={groups}
          empty={
            data.budget.expenses.length === 0 ? (
              <div className="space-y-3">
                <p className="font-semibold text-foreground">Zatím tu nejsou žádné položky</p>
                <p className="text-sm">Přidejte první výdaj nebo dodavatele, kterého domlouváte.</p>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="font-semibold text-foreground">Žádná položka neodpovídá</p>
                <p className="text-sm">Zkuste hledat jinak nebo zobrazit všechny položky.</p>
                <Button
                  appearance="outline"
                  tone="neutral"
                  size="sm"
                  onClick={() => setSearch({ filter: 'all', q: '' })}
                >
                  Zobrazit všechny položky
                </Button>
              </div>
            )
          }
        />
      )}

      {canEdit && data.budget.expenses.length < 5 ? <BudgetSuggestions onOpen={onOpen} /> : null}
    </>
  )
}

function PromptAction({
  action,
  onPaid,
  onFilter,
  onOpen,
  onAdd,
  onTotal,
}: {
  action: BudgetPromptAction
  onPaid: (expenseId: string, paymentId: string) => void
  onFilter: (filter: BudgetFilter) => void
  onOpen: (expenseId: string, tab?: ExpenseTab) => void
  onAdd: (supplierId: string) => void
  onTotal: () => void
}) {
  const props = { size: 'sm', appearance: 'outline', tone: 'neutral' } as const
  switch (action.kind) {
    case 'paid':
      return (
        <Button {...props} onClick={() => onPaid(action.expenseId, action.paymentId)}>
          <Check aria-hidden="true" className="size-3.5" />
          Označit jako zaplacené
        </Button>
      )
    case 'filter':
      return (
        <Button {...props} onClick={() => onFilter(action.filter)}>
          {action.label}
          <ArrowRight aria-hidden="true" className="size-3.5" />
        </Button>
      )
    case 'open':
      return (
        <Button {...props} onClick={() => onOpen(action.expenseId, action.tab)}>
          {action.label}
        </Button>
      )
    case 'add-supplier':
      return (
        <Button {...props} onClick={() => onAdd(action.supplierId)}>
          <Plus aria-hidden="true" className="size-3.5" />
          Přidat do rozpočtu
        </Button>
      )
    case 'total':
      return (
        <Button {...props} onClick={onTotal}>
          Upravit rozpočet
        </Button>
      )
  }
}

const items = (count: number) => `${count} ${plural(count, 'položka', 'položky', 'položek')}`

function budgetColumns(data: WeddingData): Column<Expense>[] {
  return [
    {
      key: 'item',
      header: 'Položka',
      width: 'minmax(0,1fr)',
      primary: true,
      cell: (expense) => {
        const supplier = data.suppliers.find((item) => item.id === expense.supplierId)
        return (
          <span className="block min-w-0">
            <span className="block truncate">{expenseTitle(expense, data)}</span>
            {supplier ? (
              <span className="block truncate text-xs font-normal text-muted-foreground">
                {supplierCategoryLabels[supplier.category] ?? 'Dodavatel'}
              </span>
            ) : null}
          </span>
        )
      },
    },
    {
      key: 'price',
      header: 'Cena',
      width: '8rem',
      align: 'end',
      numeric: true,
      cell: (expense) =>
        expense.amount === 0 ? (
          <span className="text-muted-foreground">Doplnit cenu</span>
        ) : (
          <span className={expense.estimate ? 'text-muted-foreground' : undefined}>
            {expense.estimate ? '≈ ' : ''}
            {formatMoney(expense.amount)}
          </span>
        ),
    },
    {
      key: 'remaining',
      header: 'Zbývá doplatit',
      width: '9rem',
      align: 'end',
      numeric: true,
      cell: (expense) => {
        if (expense.amount === 0) return <span className="text-muted-foreground">—</span>
        const remaining = Math.max(expense.amount - paidAmount(expense), 0)
        return (
          <Badge
            tone={
              remaining === 0
                ? 'success'
                : expenseStatus(expense) === 'overdue'
                  ? 'danger'
                  : 'warning'
            }
          >
            {remaining === 0 ? <Check aria-hidden="true" className="size-3.5" /> : null}
            {remaining === 0 ? 'Zaplaceno' : formatMoney(remaining)}
          </Badge>
        )
      },
    },
    {
      key: 'next',
      header: 'Další platba',
      width: '10.5rem',
      cell: (expense) => {
        const payment = nextPayment(expense)
        if (!payment) {
          const left = expense.amount - paidAmount(expense)
          return (
            <span className="text-muted-foreground">
              {expense.amount > 0 && left > 0 && !expense.estimate ? 'Nenaplánováno' : '—'}
            </span>
          )
        }
        const late = isOverdue(payment.dueDate)
        return (
          <span className="flex min-w-0 flex-col">
            <span className="truncate">
              {payment.name} · {formatMoney(payment.amount)}
            </span>
            <span
              className={late ? 'text-xs text-danger-foreground' : 'text-xs text-muted-foreground'}
            >
              {payment.dueDate
                ? late
                  ? relativeDue(payment.dueDate)
                  : formatDate(payment.dueDate)
                : 'bez splatnosti'}
            </span>
          </span>
        )
      },
    },
    {
      key: 'status',
      header: 'Stav',
      width: '8.5rem',
      cell: (expense) => {
        const status = expenseStatus(expense)
        return <Badge tone={statusTones[status]}>{statusLabels[status]}</Badge>
      },
    },
  ]
}
